/* eslint-disable no-useless-escape */
/* eslint-disable react/display-name */
import { Rate as Rating } from 'antd';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useAuth,
  useFeatureEntitlements,
  usePermissionEntitlements,
} from '@frontegg/react';
import {
  getSummaryRating,
  summaryFeedbackAction,
} from '../../store/actions/summary';
import { summaryRatingSlice } from '../../store/slices/summary';
import { endStream } from '../../utils/listen';
import { ModalComponent } from '../baseComponents';
import { MicSettings } from 'src/components/MicSettings';
import '../PastEncounter/PastEncounter.scss';
import Editor from './Editor';
import { SummaryInProgress } from '../PastEncounter/SummaryInProgress';
import { SummaryLoading } from '../PastEncounter/SummaryLoading';
import IntakeSummaryForms from '../../../appointments/components/IntakeSummaryForms';
import { isAllowedToUpdateSummary } from '../PastEncounter/permissions';
import { analytics } from 'src/services';

const NO_MEDICALNOTES_MESSAGE = '<p>No medical notes for this encounter</p>';

const RatingComponent = ({ setCommentsModelOpen, summaryRating }) => {
  const dispatch = useDispatch();

  const handleSummaryRating = (value) => {
    dispatch(summaryRatingSlice(value));
    setCommentsModelOpen(true);
  };
  const isMobile = window.innerWidth < 1260;
  return (
    <div className="flex justify-center items-center">
      <div
        className={`flex items-center bg-gray-100 p-2 rounded-lg ${
          isMobile ? '' : 'mt-1.5 '
        }`}
      >
        <span className="mr-2">Rate this note </span>
        <Rating
          onChange={handleSummaryRating}
          value={summaryRating}
          allowClear={false}
        />
      </div>
    </div>
  );
};

const SummaryCommentsModal = ({
  isOpen,
  setIsOpen,
  summaryId,
  summaryRating,
  schedulepage,
}) => {
  const dispatch = useDispatch();
  const { user } = useAuth();

  const initialValues = {
    comment: '',
  };

  const sections = [
    {
      heading: 'How can we improve?',
      className: '',
      fields: [
        {
          name: 'comment',
          type: 'textarea',
          placeholder: 'Provide additional feedback (optional)',
          rows: 6,
        },
      ],
    },
  ];

  const handleSummaryComment = async (values) => {
    const { comment } = values;
    dispatch(summaryFeedbackAction(summaryId, summaryRating, comment));
    if (schedulepage) {
      // eslint-disable-next-line no-undef
      analytics.track('Submitted Medical Notes Ratings and Comments From VCA', {
        summary_id: summaryId,
        score: summaryRating,
        comments: comment,
        email: user?.email,
      });
    } else {
      // eslint-disable-next-line no-undef
      analytics.track(
        'Submitted Medical Notes Ratings and Comments From Aura',
        {
          summary_id: summaryId,
          score: summaryRating,
          comments: comment,
          email: user?.email,
        }
      );
    }
    setIsOpen(false);
  };
  return (
    <ModalComponent
      open={isOpen}
      className={'SummaryComment-modal'}
      initialValues={initialValues}
      sections={sections}
      centered={true}
      submitButtonText="Submit"
      cancelButtonText="Cancel"
      handleCancel={() => {
        handleSummaryComment('');
        setIsOpen(false);
      }}
      handleSubmit={(values) => handleSummaryComment(values)}
    />
  );
};

const MedicalConversationBox = forwardRef(
  (
    {
      medicalConversation: medicalConversationProp = '',
      handleMedicalConversationChange,
      encounterId,
      encounterStatus,
      summaryVerbosity,
      encounterPhase,
      schedulepage,
      summaryId,
      activeButton,
      handleDictation,
      triggerShowSummaryEvidence,
      setIsSegmentDisabled,
    },
    ref
  ) => {
    const medicalConversation = medicalConversationProp?.replaceAll(
      '\n',
      '<br>'
    );
    const [ratingFeedbackOpen, setCommentsModelOpen] = useState(false);
    const { user } = useAuth();
    const { isEntitled: isDictationEntitled } =
      useFeatureEntitlements('dictation');
    const [isRatingLoading, setIsRatingLoading] = useState(true);
    const [selectedMicrophone, setSelectedMicrophone] = useState(
      localStorage.getItem('preferredMicrophoneDeviceId') || 'default'
    );
    const isMobile = window.innerWidth <= 1260;
    const { summaryRating, isSummaryListLoading } = useSelector(
      (state) => state?.summaryState
    );
    const { isEntitled: isInVisitSummaryUpdateAllowed } =
      usePermissionEntitlements('summary.in-visit.update');
    const { isEntitled: isPreChartSummaryUpdateAllowed } =
      usePermissionEntitlements('summary.pre-chart.update');
    const { isEntitled: isAfterVisitSummaryUpdateAllowed } =
      usePermissionEntitlements('summary.after-visit.update');
    const { isEntitled: isDictationSummaryUpdateAllowed } =
      usePermissionEntitlements('summary.dictate.update');
    const dispatch = useDispatch();

    const isSummaryUpdateAllowed = useMemo(() => {
      return isAllowedToUpdateSummary({
        encounterPhase,
        isInVisitSummaryUpdateAllowed,
        isPreChartSummaryUpdateAllowed,
        isAfterVisitSummaryUpdateAllowed,
        isDictationSummaryUpdateAllowed,
      });
    }, [
      encounterPhase,
      isInVisitSummaryUpdateAllowed,
      isPreChartSummaryUpdateAllowed,
      isAfterVisitSummaryUpdateAllowed,
      isDictationSummaryUpdateAllowed,
    ]);

    useEffect(() => {
      setIsRatingLoading(true);
      setIsSegmentDisabled(true);
      if (
        encounterId &&
        encounterStatus === 'completed' &&
        encounterPhase !== 'dictate'
      ) {
        dispatch(getSummaryRating(encounterId)).then(() => {
          setIsRatingLoading(false);
          setIsSegmentDisabled(false);
        });
      } else {
        setTimeout(() => {
          setIsRatingLoading(false);
          setIsSegmentDisabled(false);
        }, 500);
      }
    }, [summaryId, encounterId, dispatch]);

    const handleMicrophoneChange = async (deviceId) => {
      if (schedulepage) {
        // eslint-disable-next-line no-undef
        analytics.track(
          'Clicked Microphone Option In Microphone Setting From VCA',
          { email: user?.email }
        );
      } else {
        // eslint-disable-next-line no-undef
        analytics.track(
          'Clicked Microphone Option In Microphone Setting From Aura',
          { email: user?.email }
        );
      }
      setSelectedMicrophone(deviceId);
      localStorage.setItem('preferredMicrophoneDeviceId', deviceId);
    };

    const [dictation, setDictation] = useState('');
    const [fullDictation, setFullDictation] = useState('');
    const editorRef = useRef(null);
    const [lastInsertedText, setLastInsertedText] = useState('');

    const insertTextAtCursor = useCallback(
      (text) => {
        if (editorRef.current) {
          const newText = text.slice(lastInsertedText.length);
          editorRef.current.insertText(newText);
          if (text == '\n') {
            setLastInsertedText(text + ' ');
          } else {
            setLastInsertedText(text);
          }
        }
      },
      [lastInsertedText]
    );

    const handleSocketErrors = useCallback(() => {
      setDictation('');
      setFullDictation('');
      setLastInsertedText('');
      endStream();
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        setDictationText(text) {
          setDictation(text);
        },
        getMicrophone() {
          return selectedMicrophone;
        },
        handleSocketErrors(error) {
          handleSocketErrors(error);
        },
        stopDictation() {
          setDictation('');
          setFullDictation('');
          setLastInsertedText('');
        },
        setEditorFocus() {
          editorRef.current?.setFocus();
        },
      }),
      [selectedMicrophone, handleSocketErrors]
    );

    const applyDragonConversions = (text) => {
      const dragonText = text
        .replace(/\s?\n\s?(\w)/gi, (match, p1) => ` \n${p1.toUpperCase()}`)
        .replace(/\s?\n\n\s?(\w)/gi, (match, p1) => ` \n\n${p1.toUpperCase()}`)
        .replace(/(?:number|numerical)\s?(\d+)/gi, (match, digits) => digits)
        .replace(/\s?period/gi, () => `.`)
        .replace(/\.\s([a-zA-Z])/g, (match, p1) => `. ${p1.toUpperCase()}`)
        .replace(/\s?comma\s?/gi, () => `, `)
        .replace(/\s?semicolon/gi, () => `;`)
        .replace(/\s?colon/gi, () => `:`)
        .replace(/\:\s([a-zA-Z])/g, (match, p1) => `: ${p1.toUpperCase()}`)
        .replace(/((hyphen)|(dash))/gi, () => `-`)
        .replace(/\s?slash\s?/gi, () => `/`)
        .replace(/\s?question mark/gi, () => `?`)
        .replace(/\?\s([a-zA-Z])/g, (match, p1) => `? ${p1.toUpperCase()}`)
        .replace(/\s?exclamation (mark|point)/gi, '!')
        .replace(/\!\s([a-zA-Z])/g, (match, p1) => `! ${p1.toUpperCase()}`)
        .replace(/\s?percent\s?/gi, () => `%`)
        .replace(/(open|begin) single quote\s?/gi, "'")
        .replace(/\'\s(\w)/g, (match, p1) => `'${p1}`)
        .replace(/\s?(close|end) single quote/gi, "'")
        .replace(/(open|begin) quote\s?/gi, '"')
        .replace(/\"\s(\w)/g, (match, p1) => `"${p1}`)
        .replace(/\s?(close|end) quote/gi, '"')
        .replace(/open parenthesis\s?/gi, '(')
        .replace(/\(\s(\w)/g, (match, p1) => `(${p1}`)
        .replace(/\s?close parenthesis/gi, ')')
        .replace(/mister (\w)/gi, (match, p1) => `Mr. ${p1.toUpperCase()}`)
        .replace(/miss (\w)/gi, (match, p1) => `Ms. ${p1.toUpperCase()}`)
        .replace(/missus (\w)/gi, (match, p1) => `Mrs. ${p1.toUpperCase()}`)
        .replace(/doctor (\w)/gi, (match, p1) => `Dr. ${p1.toUpperCase()}`)
        .replace(/i\s/gi, 'I ')
        .replace(/i'/gi, "I'")
        .replace(
          /no\s?caps\s?on[^a-zA-Z0-9]*(.*?)no\s?caps\s?off?/gi,
          (match, p1) => p1.trim().toLowerCase()
        )
        .replace(
          /all\s?caps\s?on[^a-zA-Z0-9]*(.*?)all\s?caps\s?off?/gi,
          (match, p1) => p1.trim().toUpperCase()
        )
        .replace(/caps\s?on[^a-zA-Z0-9]*(.*?)caps\s?off?/gi, (match, p1) =>
          p1.trim().replace(/\b\w/g, (c) => c.toUpperCase())
        )
        .replace(/\?+/g, '?');

      return dragonText;
    };

    useEffect(() => {
      if (dictation.length > 0 && dictation[0].text.length > 0) {
        if (fullDictation.length > 0) {
          let newText = fullDictation + ' ' + dictation[0].text;
          setFullDictation(applyDragonConversions(newText));
        } else {
          let newText =
            dictation[0].text.charAt(0).toUpperCase() +
            dictation[0].text.slice(1);
          setFullDictation(applyDragonConversions(newText));
        }
      }
    }, [dictation]);

    const outerContainerRef = useRef(null);

    useEffect(() => {
      const scroll = parseInt(localStorage.getItem('editorScrollPosition'));
      if (!isNaN(scroll) && outerContainerRef.current) {
        setTimeout(() => {
          outerContainerRef.current.scrollTop = scroll;
          localStorage.removeItem('editorScrollPosition');
        }, 50);
      }
    }, [medicalConversation]);

    useEffect(() => {
      if (fullDictation.length > 0) {
        insertTextAtCursor(fullDictation);
      }
    }, [fullDictation, insertTextAtCursor]);
    return (
      <>
        {encounterPhase === 'dictate' &&
          encounterPhase !== 'after-visit' &&
          isDictationEntitled && (
            <div className="absolute top-[-50px] md:top-[-70px] lg:top-2 right-3">
              <MicSettings
                selectedMicrophone={selectedMicrophone}
                onSelectionChange={handleMicrophoneChange}
              />
            </div>
          )}

        {encounterStatus === 'summary_inprogress' ? (
          <SummaryInProgress
            encounter_id={encounterId}
            schedule_page={schedulepage}
            encounter_phase={encounterPhase}
            summary_verbosity={summaryVerbosity}
          />
        ) : isSummaryListLoading && !medicalConversation ? (
          <SummaryLoading />
        ) : isMobile ? (
          <div className="mr-2 h-full">
            <div
              className="relative flex-1 overflow-y-auto"
              style={{ minHeight: '65vh' }}
              ref={outerContainerRef}
            >
              <Editor
                ref={editorRef}
                outerContainerRef={outerContainerRef}
                value={
                  medicalConversation === '' && encounterStatus === 'completed'
                    ? NO_MEDICALNOTES_MESSAGE
                    : medicalConversation
                }
                showPlaceholder={
                  (encounterPhase === 'dictate' &&
                    medicalConversation === '') ||
                  medicalConversation === '<p><br></p>'
                }
                onChange={handleMedicalConversationChange}
                encounterId={encounterId}
                theme="snow"
                className="ql-toolbar font-sans rounded-b-xl"
                style={{ border: 'none', height: '100%' }}
                enableDotPhrases={true}
                readOnly={
                  !isSummaryUpdateAllowed ||
                  (encounterStatus !== 'completed' &&
                    encounterPhase !== 'dictate')
                }
                handleDictation={handleDictation}
                triggerShowSummaryEvidence={triggerShowSummaryEvidence}
                showSourceOption={encounterPhase !== 'dictate'}
              />
              {schedulepage && <IntakeSummaryForms />}
            </div>
            {encounterStatus === 'completed' &&
              medicalConversation &&
              medicalConversation !== NO_MEDICALNOTES_MESSAGE &&
              activeButton === 'In visit' && (
                <>
                  <RatingComponent
                    summaryId={summaryId}
                    encounterId={encounterId}
                    schedulepage={schedulepage}
                    setCommentsModelOpen={setCommentsModelOpen}
                    summaryRating={summaryRating}
                  />
                  <SummaryCommentsModal
                    isOpen={ratingFeedbackOpen}
                    setIsOpen={setCommentsModelOpen}
                    summaryId={summaryId}
                    summaryRating={summaryRating}
                    schedulepage={schedulepage}
                  />
                </>
              )}
          </div>
        ) : (
          <>
            <div
              className={`h-full overflow-auto ${
                schedulepage ? ' bg-white' : ''
              }`}
              ref={outerContainerRef}
            >
              <Editor
                ref={editorRef}
                outerContainerRef={outerContainerRef}
                value={
                  medicalConversation === '' && encounterStatus === 'completed'
                    ? NO_MEDICALNOTES_MESSAGE
                    : medicalConversation
                }
                showPlaceholder={
                  (encounterPhase === 'dictate' &&
                    medicalConversation === '') ||
                  medicalConversation === '<p><br></p>'
                }
                onChange={handleMedicalConversationChange}
                encounterId={encounterId}
                theme="snow"
                className={`ql-toolbar z-10 font-sans`}
                style={{ border: 'none', height: '100%' }}
                enableDotPhrases={true}
                readOnly={
                  !isSummaryUpdateAllowed ||
                  (encounterStatus !== 'completed' &&
                    encounterPhase !== 'dictate')
                }
                handleDictation={handleDictation}
                triggerShowSummaryEvidence={triggerShowSummaryEvidence}
                showSourceOption={encounterPhase !== 'dictate'}
              />
              {schedulepage && encounterPhase == 'in-visit' && (
                <IntakeSummaryForms />
              )}
            </div>
            {encounterStatus === 'completed' &&
              medicalConversation &&
              medicalConversation !== NO_MEDICALNOTES_MESSAGE &&
              activeButton === 'In visit' && (
                <div className="w-full flex-0 rounded-b-xl">
                  <RatingComponent
                    summaryId={summaryId}
                    encounterId={encounterId}
                    schedulepage={schedulepage}
                    setCommentsModelOpen={setCommentsModelOpen}
                    summaryRating={summaryRating}
                  />
                  <SummaryCommentsModal
                    isOpen={ratingFeedbackOpen}
                    setIsOpen={setCommentsModelOpen}
                    summaryId={summaryId}
                    summaryRating={summaryRating}
                    schedulepage={schedulepage}
                  />
                </div>
              )}
          </>
        )}
      </>
    );
  }
);

export default MedicalConversationBox;
